import { httpClient, toClientApiError } from "./httpClient";

export type PlatformTenant = {
  id: string;
  name: string;
  slug: string;
  code: number;
};

export type PlatformPack = {
  key: string;
  displayName: string;
  description: string;
  labels: {
    customer: string;
    customers: string;
    customerNew: string;
    project: string;
    projects: string;
  };
};

export type PlatformModuleCatalogItem = {
  key: string;
  displayNameHe: string;
  descriptionHe: string;
  features: string[];
  defaultFeatures: string[];
};

export type TenantModuleView = {
  key: string;
  displayNameHe: string;
  status: "active" | "disabled";
  features: string[];
};

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

export const platformApi = {
  listTenants(): Promise<PlatformTenant[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: PlatformTenant[] }>("/platform/tenants");
      return res.data.items;
    });
  },

  listPacks(): Promise<PlatformPack[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: PlatformPack[] }>("/platform/packs");
      return res.data.items;
    });
  },

  createTenant(input: {
    name: string;
    slug: string;
    packKey?: string;
    adminEmail?: string;
    adminPassword?: string;
    adminDisplayName?: string;
  }): Promise<{
    id: string;
    name: string;
    slug: string;
    code: number;
    packKey: string | null;
    admin: { userId: string; email: string; membershipId: string } | null;
  }> {
    return wrap(async () => {
      const res = await httpClient.post<{
        item: {
          id: string;
          name: string;
          slug: string;
          code: number;
          packKey: string | null;
          admin: { userId: string; email: string; membershipId: string } | null;
        };
      }>("/platform/tenants", input);
      return res.data.item;
    });
  },

  disableTenant(tenantId: string): Promise<{
    id: string;
    name: string;
    slug: string;
    code: number;
    status: string;
  }> {
    return wrap(async () => {
      const res = await httpClient.post<{
        item: {
          id: string;
          name: string;
          slug: string;
          code: number;
          status: string;
        };
      }>(`/platform/tenants/${tenantId}/disable`, {});
      return res.data.item;
    });
  },

  listModuleCatalog(): Promise<PlatformModuleCatalogItem[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: PlatformModuleCatalogItem[] }>(
        "/platform/module-catalog",
      );
      return res.data.items;
    });
  },

  listTenantModules(tenantId: string): Promise<{
    modules: TenantModuleView[];
    enabledFeatures: string[];
  }> {
    return wrap(async () => {
      const res = await httpClient.get<{
        modules: TenantModuleView[];
        enabledFeatures: string[];
      }>(`/platform/tenants/${tenantId}/modules`);
      return res.data;
    });
  },

  setTenantModule(
    tenantId: string,
    body: {
      moduleKey: string;
      status: "active" | "disabled";
      features?: string[];
    },
  ): Promise<{ key: string; status: string; features: string[] }> {
    return wrap(async () => {
      const res = await httpClient.put<{
        item: { key: string; status: string; features: string[] };
      }>(`/platform/tenants/${tenantId}/modules`, body);
      return res.data.item;
    });
  },
};
